if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const submittedVariantId = Number(formData.get('id')) || Number(this.variantIdInput?.value) || 0;
        const normalizeSellingPlanValue = (value) => {
          const raw = String(value || '').trim();
          if (!raw) return null;

          const direct = Number(raw);
          if (Number.isInteger(direct) && direct > 0) return String(direct);

          const matches = raw.match(/\d+/g);
          if (!matches || !matches.length) return null;
          const extracted = Number(matches[matches.length - 1]);
          return Number.isInteger(extracted) && extracted > 0 ? String(extracted) : null;
        };
        const getVariantSellingPlanIds = (variantId) => {
          if (!(Number(variantId) > 0)) return null;
          try {
            const api = window.BumpSubscription;
            if (api && typeof api.getValidSellingPlanIdsForVariant === 'function') {
              const fromApi = api.getValidSellingPlanIdsForVariant(variantId);
              if (Array.isArray(fromApi)) {
                return fromApi.map(normalizeSellingPlanValue).filter((value) => !!value);
              }
            }
          } catch (e) {
            // Fall through to DOM JSON fallback.
          }

          try {
            const plansScript = document.getElementById('bump-product-selling-plans');
            if (!plansScript) return null;
            const parsed = JSON.parse(plansScript.textContent || '{}');
            const variants = Array.isArray(parsed && parsed.variants) ? parsed.variants : null;
            if (!variants) return null;
            const variantObj = variants.find((variant) => Number(variant && variant.id) === Number(variantId));
            if (!variantObj || !Array.isArray(variantObj.selling_plan_allocations)) return null;
            const allocations = variantObj.selling_plan_allocations;
            if (!allocations.length) return [];
            const normalizedAllocations = allocations
              .map((allocation) =>
                normalizeSellingPlanValue(
                  allocation && (
                    allocation.selling_plan_id ||
                    (allocation.selling_plan && allocation.selling_plan.id) ||
                    allocation.id
                  )
                )
              )
              .filter((value) => !!value);
            return normalizedAllocations.length ? normalizedAllocations : null;
          } catch (e) {
            return null;
          }
        };

        let normalizedSellingPlan = formData
          .getAll('selling_plan')
          .map(normalizeSellingPlanValue)
          .find((value) => !!value);
        if (normalizedSellingPlan) {
          const validPlanIdsForVariant = getVariantSellingPlanIds(submittedVariantId);
          if (Array.isArray(validPlanIdsForVariant)) {
            if (!validPlanIdsForVariant.length) {
              normalizedSellingPlan = null;
            } else if (!validPlanIdsForVariant.includes(normalizedSellingPlan)) {
              normalizedSellingPlan = null;
            }
          }
        }
        if (formData.getAll('selling_plan').length) {
          formData.delete('selling_plan');
          if (normalizedSellingPlan) formData.set('selling_plan', normalizedSellingPlan);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: submittedVariantId || formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: submittedVariantId || formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
